/**
 * Empty draft — the only definition of a clean analysis slate.
 *
 * New and boot-time "restored deal is missing" both apply this. They must
 * not keep separate copies of the field list: those copies already drifted
 * (New reset last-op currency and identity CHF FX; the 404 wipe did not).
 *
 * Session control stays with the caller and is intentionally not in here:
 * - New flushes a pending save of the deal being left, bumps the session
 *   epoch, and marks boot finished.
 * - A missing deal must not flush (that id is gone — a flush could write the
 *   orphan buffer). It also must not bump the epoch; the boot effect's fence
 *   decides whether this reset is still the current session.
 * - Unmount bumps the epoch from the effect cleanup so a late result cannot
 *   setState without Open or New.
 *
 * Clearing currentFileId without this reset left rich local data on screen.
 * Save then treated the session as a new draft and POST /api/deals created
 * a lookalike duplicate.
 */
import { hydrateScenarios } from './dealScenarios';
import { hydrateLandAssessmentStore } from './landAssessment';
import { hydrateStageFinanceAssumptionsStore } from './stageFinanceAssumptions';

/** Operating currency stamped onto an empty draft's FX gate (New's rule). */
export const EMPTY_DRAFT_OPERATING_CURRENCY = 'CHF';

/**
 * Live CHF→CHF identity. A new or wiped draft shows this, not the previous
 * deal's rate. Fresh object so callers cannot share one mutable record.
 */
export function identityChfFx() {
  return {
    base: 'CHF',
    quote: 'CHF',
    rate: 1,
    source: 'identity',
    mode: 'live',
    stale: false,
  };
}

/**
 * Working state for an empty draft.
 * `inputs` / `assumptions` are the fresh Swiss seed (not the deal being left).
 * `emptyOutputs` / `emptyDecision` are the calculation shells the page shows
 * before the backend responds.
 */
export function materializeEmptyDraft({
  inputs,
  assumptions,
  emptyOutputs,
  emptyDecision,
}) {
  if (inputs == null || assumptions == null) {
    throw new Error('empty draft requires fresh inputs and assumptions');
  }
  if (emptyOutputs == null || emptyDecision == null) {
    throw new Error('empty draft requires empty calculation shells');
  }

  const hydrated = hydrateScenarios({ inputs, assumptions });
  return {
    inputs,
    assumptions,
    scenarios: hydrated.scenarios,
    activeScenarioId: hydrated.activeScenarioId,
    cashTracker: null,
    landAssessment: hydrateLandAssessmentStore(null),
    stageFinanceAssumptions: hydrateStageFinanceAssumptionsStore(null),
    workspace: 'evaluation',
    notes: '',
    cadastreData: null,
    marketData: null,
    fxSnapshot: null,
    currentFileId: null,
    currentFileName: null,
    draftClampedNotice: false,
    // Hardcoded CHF, matching New — not read from the seed assumptions.
    // The calc effect compares this ref to the new assumptions; leaving the
    // previous deal's currency here would replace identity FX with a pending rate.
    lastOpCurrency: EMPTY_DRAFT_OPERATING_CURRENCY,
    fx: identityChfFx(),
    outputs: emptyOutputs,
    outputsChf: {},
    decision: emptyDecision,
    qualityGrade: {},
    // No file id and no queued payload: server autosave cannot POST/PUT this slate.
    suppressServerAutosave: false,
    lastPersistedFingerprint: null,
    dirtyServerPayload: null,
    dirtyServerFileId: null,
  };
}

/**
 * Apply `materializeEmptyDraft` to analysis state. Both New and the
 * missing-deal boot path call this so the written fields cannot diverge.
 */
export function applyEmptyDraft(draft, sinks) {
  sinks.suppressServerAutosaveRef.current = draft.suppressServerAutosave;
  sinks.lastPersistedFingerprintRef.current = draft.lastPersistedFingerprint;
  sinks.dirtyServerPayloadRef.current = draft.dirtyServerPayload;
  sinks.dirtyServerFileIdRef.current = draft.dirtyServerFileId;
  sinks.lastOpCurrencyRef.current = draft.lastOpCurrency;

  sinks.setScenarios(draft.scenarios);
  sinks.setActiveScenarioId(draft.activeScenarioId);
  sinks.setCashTracker(draft.cashTracker);
  sinks.setLandAssessment(draft.landAssessment);
  sinks.setStageFinanceAssumptions(draft.stageFinanceAssumptions);
  sinks.setWorkspace(draft.workspace);
  sinks.setInputs(draft.inputs);
  sinks.setAssumptionsRaw(draft.assumptions);
  sinks.setNotes(draft.notes);
  sinks.setCadastreData(draft.cadastreData);
  sinks.setMarketData(draft.marketData);
  sinks.setFxSnapshot(draft.fxSnapshot);
  sinks.setCurrentFileId(draft.currentFileId);
  sinks.setCurrentFileName(draft.currentFileName);
  sinks.setDraftClampedNotice(draft.draftClampedNotice);
  sinks.setFx(draft.fx);
  sinks.setOutputs(draft.outputs);
  sinks.setOutputsChf(draft.outputsChf);
  sinks.setDecision(draft.decision);
  sinks.setQualityGrade(draft.qualityGrade);
  sinks.setProjectVersion((v) => v + 1);
}
